import { io, Socket } from "socket.io-client";
import mitt, { Emitter } from "mitt";
import { SocketEventType, SocketEvents, AuctionUpdate } from "fungi-types";

export class SocketService {
  private socket: Socket | null = null;
  public emitter: Emitter<SocketEvents>;
  private isInitialized = false;
  private userJwt: string | null = null;

  // Connection state
  public isConnected = false;
  public isReconnecting = false;
  public lastAuctionUpdate: AuctionUpdate | null = null;

  constructor() {
    this.emitter = mitt<SocketEvents>();
  }

  public initialize(url: string = "https://ws.fungiball.io"): void {
    if (this.isInitialized || this.socket?.connected) return;

    console.log("🔄 Initializing WebSocket connection...");
    this.isInitialized = true;

    this.socket = io(url, {
      reconnection: true,
      reconnectionDelay: 500,
      reconnectionDelayMax: 3000,
      reconnectionAttempts: Infinity,
      timeout: 20000,
      forceNew: false,
    });

    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    if (!this.socket) return;

    this.socket.on("connect", () => {
      console.log("✅ Connected to WebSocket server");
      this.isConnected = true;
      this.isReconnecting = false;
      this.handleConnectionEstablished();

      // this.emitter.emit("socket:connected");
    });

    this.socket.on("disconnect", (reason: string) => {
      console.log("❌ Disconnected from WebSocket server:", reason);
      this.isConnected = false;
      // this.emitter.emit("socket:disconnected", reason);

      if (reason !== "io client disconnect") {
        this.isReconnecting = true;
      }
    });

    this.socket.on("reconnect", (attemptNumber: number) => {
      console.log(`🔄 Reconnected after ${attemptNumber} attempts`);
      this.isConnected = true;
      this.isReconnecting = false;
      this.handleConnectionEstablished();
      // this.emitter.emit("socket:reconnected", attemptNumber);
    });

    this.socket.on("reconnect_attempt", (attemptNumber: number) => {
      console.log(`🔄 Reconnection attempt ${attemptNumber}`);
      this.isReconnecting = true;
      // this.emitter.emit("socket:reconnecting", attemptNumber);
    });

    this.socket.on("reconnect_error", (error: Error) => {
      console.error("❌ Reconnection failed:", error);
    });

    this.socket.on("reconnect_failed", () => {
      console.error("❌ Reconnection failed permanently");
      this.isReconnecting = false;
    });
    // === Typed app events ===
    const appEvents: {
      [K in keyof SocketEvents]?: {
        label: string;
        log?: "info" | "error";
      };
    } = {
      [SocketEventType.AUCTION_UPDATED]: { label: "Auction updated" },
      [SocketEventType.AUCTION_WON]: { label: "Auction won" },
      [SocketEventType.USER_OUTBID]: { label: "User has been outbid" },
      [SocketEventType.POL_RATES_UPDATED]: { label: "POL rates updated" },
      [SocketEventType.PACKS_STOCK_UPDATED]: { label: "Packs stock updated" },
      [SocketEventType.USER_TOKEN_BALANCE_UPDATED]: { label: "User token balance updated" },
      [SocketEventType.USER_CREDIT_BALANCE_UPDATED]: { label: "User credit balance updated" },
      [SocketEventType.CANT_BID_WITH_STRIPE]: { label: "Can't bid with Stripe" },
      [SocketEventType.NOTIFICATIONS_REFRESH]: { label: "Notifications refresh event received" },
      [SocketEventType.USER_CARDS_UPDATED]: { label: "User cards updated" },
    };

    // Register all events dynamically
    (
      Object.entries(appEvents) as [keyof SocketEvents, { label: string; log?: "info" | "error" }][]
    ).forEach(([event, { label, log = "info" }]) => {
      this.socket!.on(event, (data: SocketEvents[typeof event]) => {
        // if (log === "error") {
        //   console.error(`❌ ${label}:`, data);
        // } else {
        //   console.log(`💬 ${label}:`, data);
        // }
        this.emitter.emit(event, data);
      });
    });

    this.socket.on("connect_error", (error: Error) => {
      console.error("❌ Connection error:", error);
      this.isReconnecting = true;
    });
  }

  private handleConnectionEstablished(): void {
    if (this.userJwt) {
      console.log("🔄 Processing user room join...");
      this.joinUserRoom(this.userJwt);
    }
  }

  public connect(): void {
    if (!this.isInitialized) {
      this.initialize();
    } else if (this.socket && !this.socket.connected) {
      this.socket.connect();
    }
  }

  public disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.isConnected = false;
      this.isReconnecting = false;
    }
  }

  public getConnectionStatus(): string {
    if (this.isConnected) return "Connected";
    if (this.isReconnecting) return "Reconnecting...";
    return "Disconnected";
  }

  public joinUserRoom(jwt: string): void {
    this.userJwt = jwt;
    if (this.isConnected && this.socket) {
      this.socket.emit("join-user-room", jwt);
    } else {
      console.log("⏳ Socket not ready, queuing user room join...");
    }
  }

  // Event subscription methods
  public on<K extends keyof SocketEvents>(
    event: K,
    handler: (data: SocketEvents[K]) => void
  ): void {
    this.emitter.on(event, handler);
  }

  public off<K extends keyof SocketEvents>(
    event: K,
    handler?: (data: SocketEvents[K]) => void
  ): void {
    this.emitter.off(event, handler);
  }

  public getEmitter(): Emitter<SocketEvents> {
    return this.emitter;
  }
}

// Create singleton instance
const socketService = new SocketService();

export default socketService;
